import { Routes } from '@angular/router';

import { hostTenantMatch, hostTenantResolver } from './features/storefront/data/host-tenant.resolver';
import { StorefrontStore } from './features/storefront/state/storefront.store';

/**
 * Brijj Finds marketplace route table.
 *
 * Public-first: browsing, cart and checkout need no authentication. Signed-in
 * surfaces (favorites, account) attach `shopperAuthGuard` on their own feature
 * routes. Feature modules are added phase by phase.
 */
export const routes: Routes = [
  {
    path: '',
    children: [
      {
        // On a tenant's own subdomain the root IS that tenant's storefront
        // (SM-02). Declared before the marketplace home so it wins when the
        // host resolved to a tenant; `canMatch` falls through otherwise.
        // Scoped to the empty path only - giving it children would let a
        // `:storeSlug` param swallow `/cart`, `/search` and friends.
        path: '',
        pathMatch: 'full',
        canMatch: [hostTenantMatch],
        providers: [StorefrontStore],
        resolve: { tenant: hostTenantResolver },
        loadComponent: () =>
          import('./features/storefront/pages/storefront-home-page/storefront-home-page.component').then(
            m => m.StorefrontHomePageComponent
          )
      },
      {
        path: '',
        loadChildren: () => import('./features/home/home.routes').then(m => m.HOME_ROUTES)
      },
      {
        path: 'sign-in',
        loadChildren: () => import('./features/auth/auth.routes').then(m => m.AUTH_ROUTES)
      },
      {
        path: 'sign-up',
        loadChildren: () => import('./features/auth/auth.routes').then(m => m.SIGN_UP_ROUTES)
      },
      {
        path: 'forgot-password',
        loadChildren: () => import('./features/auth/auth.routes').then(m => m.FORGOT_PASSWORD_ROUTES)
      },
      {
        path: 'orders/lookup',
        loadChildren: () => import('./features/account/account.routes').then(m => m.ORDER_LOOKUP_ROUTES)
      },
      {
        path: 'search',
        loadChildren: () => import('./features/browse/browse.routes').then(m => m.BROWSE_ROUTES)
      },
      {
        path: 'item/:slugId',
        loadChildren: () => import('./features/listing-detail/listing-detail.routes').then(m => m.LISTING_DETAIL_ROUTES)
      },
      {
        path: 'cart',
        loadChildren: () => import('./features/cart/cart.routes').then(m => m.CART_ROUTES)
      },
      {
        path: 'checkout',
        loadChildren: () => import('./features/checkout/checkout.routes').then(m => m.CHECKOUT_ROUTES)
      },
      {
        // Favorites moved inside the account shell so it reads as a tab, not a
        // separate destination. The old path stays as a redirect: deep links,
        // the "My Hunts" subnav entry and sign-in returnUrls all still use it,
        // and Angular carries the query string (`?tab=hunts`) across.
        path: 'favorites',
        pathMatch: 'full',
        redirectTo: 'account/favorites'
      },
      {
        path: 'account',
        loadChildren: () => import('./features/account/account.routes').then(m => m.ACCOUNT_ROUTES)
      },
      {
        // A tenant is a retailer COMPANY, so its storefront mounts under
        // `retailer` (deliberate deviation from HLD SM-06's `/store/{slug}`
        // example - the `store` segment names physical locations only).
        path: 'retailer/:tenantSlug',
        loadChildren: () => import('./features/storefront/storefront.routes').then(m => m.STOREFRONT_ROUTES)
      },
      // Legacy mount: storefronts shipped at `/store/…` before the rename -
      // param substitution keeps old deep links working.
      { path: 'store/:tenantSlug/:storeSlug', pathMatch: 'full', redirectTo: 'retailer/:tenantSlug/:storeSlug' },
      { path: 'store/:tenantSlug', pathMatch: 'full', redirectTo: 'retailer/:tenantSlug' },
      {
        path: 'sell/apply',
        loadChildren: () => import('./features/marketing/marketing.routes').then(m => m.SELL_APPLY_ROUTES)
      },
      {
        path: 'sell',
        loadChildren: () => import('./features/marketing/marketing.routes').then(m => m.SELL_ROUTES)
      },
      {
        path: 'help',
        loadChildren: () => import('./features/marketing/marketing.routes').then(m => m.HELP_ROUTES)
      },
      {
        path: 'privacy',
        loadChildren: () => import('./features/marketing/marketing.routes').then(m => m.PRIVACY_ROUTES)
      },
      {
        path: 'terms',
        loadChildren: () => import('./features/marketing/marketing.routes').then(m => m.TERMS_ROUTES)
      },
      {
        path: 'why-brijj',
        loadChildren: () => import('./features/marketing/marketing.routes').then(m => m.WHY_ROUTES)
      },
      {
        // Public hunt sign-up (CAT-06). The same page also mounts inside the
        // storefront, where the tenant scope rebrands it.
        path: 'thriftspotters',
        loadChildren: () => import('./features/thriftspotters/thriftspotters.routes').then(m => m.THRIFTSPOTTERS_ROUTES)
      },
      {
        // Explicit 404 target (e.g. the storefront resolver's unknown-retailer
        // redirect); rendered inside the shell so search and navigation stay.
        path: 'not-found',
        loadComponent: () =>
          import('./shared/components/not-found/not-found-page.component').then(m => m.NotFoundPageComponent)
      }
    ]
  },
  {
    path: '**',
    loadComponent: () =>
      import('./shared/components/not-found/not-found-page.component').then(m => m.NotFoundPageComponent)
  }
];
